// src/pages/privacy.tsx

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Shield,
  Mail,
  MapPin,
  ArrowUp,
  ChevronRight,
  FileText,
} from "lucide-react";
import "../styles/pages/legal/legal-pages.css";

const PrivacyPage: React.FC = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const sections = [
    { id: "about", title: "About Orra Kingdom" },
    { id: "information-collected", title: "Information We Collect" },
    { id: "church-affiliation", title: "Church & Community Information" },
    { id: "identity-verification", title: "Identity Verification" },
    { id: "transaction-info", title: "Transaction Information" },
    { id: "automatic-info", title: "Automatically Collected Information" },
    { id: "payment-info", title: "Payment Information" },
    { id: "how-we-use", title: "How We Use Personal Information" },
    { id: "legal-basis", title: "Legal Basis for Processing" },
    { id: "data-sharing", title: "Data Sharing and Disclosure" },
    { id: "international-transfers", title: "International Data Transfers" },
    { id: "data-retention", title: "Data Retention" },
    { id: "data-security", title: "Data Security" },
    { id: "trust-scoring", title: "Trust Scoring Systems" },
    { id: "user-rights", title: "User Rights" },
    { id: "children-privacy", title: "Children's Privacy" },
    { id: "cookies", title: "Cookies and Tracking" },
    { id: "third-party", title: "Third-Party Links" },
    { id: "changes", title: "Changes to Privacy Policy" },
    { id: "contact", title: "Contact Information" },
  ];

  return (
    <div className="legal-page-container">
      {/* Background Effects - Glassmorphism Layers */}
      <div className="legal-background">
        <div className="legal-glow legal-glow-1" />
        <div className="legal-glow legal-glow-2" />
        <div className="legal-glow legal-glow-3" />
      </div>

      <div className="legal-content-wrapper">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="legal-card"
        >
          {/* Header */}
          <div className="legal-header">
            <div className="legal-meta">
              <span className="legal-badge">
                <Shield size={14} />
                Privacy Policy
              </span>
              <span className="legal-date">
                Last Updated: 23rd February 2026
              </span>
            </div>
            <h1 className="legal-title">Your Data, Protected</h1>
            <p className="legal-subtitle">
              How Orra Kingdom collects, uses, and safeguards your personal
              information
            </p>
          </div>

          {/* Table of Contents */}
          <div className="legal-toc">
            <div className="legal-toc-title">Table of Contents</div>
            <div className="legal-toc-grid">
              {sections.map((section, index) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="legal-toc-item"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById(section.id)?.scrollIntoView({
                      behavior: "smooth",
                    });
                  }}
                >
                  <ChevronRight size={12} />
                  <span>
                    {index + 1}. {section.title}
                  </span>
                </a>
              ))}
            </div>
          </div>

          {/* Introduction */}
          <div className="legal-section" id="introduction">
            <div className="legal-section-content">
              <div className="legal-highlight">
                <p>
                  <strong>Your privacy matters to us.</strong> This Privacy
                  Policy explains how Orra Kingdom Limited collects, uses,
                  stores, protects, and shares personal information when
                  individuals access or use the Platform.
                </p>
                <p style={{ marginTop: "1rem", marginBottom: 0 }}>
                  We are committed to protecting personal data in accordance
                  with the Data Protection Act, 2019 (Kenya) and applicable
                  international data protection principles.
                </p>
              </div>
            </div>
          </div>

          <div className="legal-divider" />

          {/* Section 1 - About Orra Kingdom */}
          <div className="legal-section" id="about">
            <div className="legal-section-header">
              <span className="legal-section-number">1</span>
              <h2 className="legal-section-title">About Orra Kingdom</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                Orra Kingdom Limited is a private limited liability company
                incorporated under the laws of the Republic of Kenya.
              </p>
              <p className="legal-paragraph">
                Although initial operational activities may begin within Kenya,
                the Platform is designed for participation by churches,
                organizations, businesses, and individuals across Africa and
                internationally wherever legally permitted.
              </p>
              <p className="legal-paragraph">
                Where required by applicable law, Orra Kingdom acts as a Data
                Controller in relation to personal data collected through the
                Platform.
              </p>
            </div>
          </div>

          {/* Section 2 - Information We Collect */}
          <div className="legal-section" id="information-collected">
            <div className="legal-section-header">
              <span className="legal-section-number">2</span>
              <h2 className="legal-section-title">Information We Collect</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                We collect personal information necessary to operate a trusted
                commerce ecosystem. Information may be collected directly from
                users, through automated technologies, or through authorized
                verification partners.
              </p>

              <p
                className="legal-paragraph"
                style={{ marginTop: "1.5rem", fontWeight: 600 }}
              >
                2.1 Information You Provide Directly
              </p>
              <p className="legal-paragraph">
                When creating or using an account, we may collect:
              </p>
              <ul className="legal-list">
                <li className="legal-list-item">Full name.</li>
                <li className="legal-list-item">Email address.</li>
                <li className="legal-list-item">Telephone number.</li>
                <li className="legal-list-item">
                  Residential or business address.
                </li>
                <li className="legal-list-item">Profile photographs.</li>
                <li className="legal-list-item">
                  Professional qualifications or certifications.
                </li>
                <li className="legal-list-item">
                  Business registration information.
                </li>
                <li className="legal-list-item">
                  Payment information where applicable.
                </li>
              </ul>
              <p className="legal-paragraph">
                Users may also voluntarily provide portfolios, listings,
                communications, customer feedback, or reviews.
              </p>
            </div>
          </div>

          {/* Section 3 - Church Affiliation */}
          <div className="legal-section" id="church-affiliation">
            <div className="legal-section-header">
              <span className="legal-section-number">3</span>
              <h2 className="legal-section-title">
                Church or Community Affiliation Information
              </h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                Because the Platform facilitates faith-based community
                participation, users may provide or verify:
              </p>
              <ul className="legal-list">
                <li className="legal-list-item">
                  Church or faith organization affiliation.
                </li>
                <li className="legal-list-item">Membership confirmation.</li>
                <li className="legal-list-item">Referral endorsements.</li>
              </ul>
              <p className="legal-paragraph">
                This information is used solely for verification and community
                participation features. The Company does not use religious
                affiliation information for advertising profiling unrelated to
                Platform functionality.
              </p>
            </div>
          </div>

          {/* Section 4 - Identity Verification */}
          <div className="legal-section" id="identity-verification">
            <div className="legal-section-header">
              <span className="legal-section-number">4</span>
              <h2 className="legal-section-title">
                Identity Verification Information
              </h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                To maintain trust and prevent fraud, we may collect:
              </p>
              <ul className="legal-list">
                <li className="legal-list-item">
                  Government-issued identification documents.
                </li>
                <li className="legal-list-item">
                  Business registration certificates.
                </li>
                <li className="legal-list-item">Professional licenses.</li>
              </ul>
              <p className="legal-paragraph">
                Verification may occur through secure third-party identity
                verification providers.
              </p>
            </div>
          </div>

          {/* Section 5 - Transaction Information */}
          <div className="legal-section" id="transaction-info">
            <div className="legal-section-header">
              <span className="legal-section-number">5</span>
              <h2 className="legal-section-title">Transaction Information</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                When users engage in commerce through the Platform, we may
                collect:
              </p>
              <ul className="legal-list">
                <li className="legal-list-item">
                  Purchase or service details.
                </li>
                <li className="legal-list-item">Payment records.</li>
                <li className="legal-list-item">Delivery confirmations.</li>
                <li className="legal-list-item">
                  Communication records relating to agreements.
                </li>
              </ul>
              <p className="legal-paragraph">
                Transaction records help ensure accountability and dispute
                resolution.
              </p>
            </div>
          </div>

          {/* Section 6 - Automatically Collected Information */}
          <div className="legal-section" id="automatic-info">
            <div className="legal-section-header">
              <span className="legal-section-number">6</span>
              <h2 className="legal-section-title">
                Automatically Collected Information
              </h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                When using the Platform, we may automatically collect:
              </p>
              <ul className="legal-list">
                <li className="legal-list-item">Device identifiers.</li>
                <li className="legal-list-item">IP address.</li>
                <li className="legal-list-item">Browser type.</li>
                <li className="legal-list-item">Operating system.</li>
                <li className="legal-list-item">Usage behavior.</li>
                <li className="legal-list-item">Interaction logs.</li>
              </ul>
              <p className="legal-paragraph">
                This information supports security monitoring and system
                improvement.
              </p>
            </div>
          </div>

          {/* Section 7 - Payment Information */}
          <div className="legal-section" id="payment-info">
            <div className="legal-section-header">
              <span className="legal-section-number">7</span>
              <h2 className="legal-section-title">Payment Information</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                Payments may be processed through licensed third-party financial
                institutions or payment processors. We generally do not store
                full card or sensitive payment credentials directly.
              </p>
              <p className="legal-paragraph">
                Payment partners may collect additional information under their
                own privacy policies.
              </p>
            </div>
          </div>

          {/* Section 8 - How We Use Information */}
          <div className="legal-section" id="how-we-use">
            <div className="legal-section-header">
              <span className="legal-section-number">8</span>
              <h2 className="legal-section-title">
                How We Use Personal Information
              </h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                We process personal data only where necessary to operate the
                Platform or comply with legal obligations. Uses may include:
              </p>
              <ul className="legal-list">
                <li className="legal-list-item">
                  Creating and maintaining accounts.
                </li>
                <li className="legal-list-item">
                  Verifying identity and preventing fraud.
                </li>
                <li className="legal-list-item">
                  Confirming church or community affiliation.
                </li>
                <li className="legal-list-item">
                  Facilitating transactions between users.
                </li>
                <li className="legal-list-item">
                  Processing payments and withdrawals.
                </li>
                <li className="legal-list-item">
                  Providing dispute resolution services.
                </li>
                <li className="legal-list-item">
                  Operating trust and reputation scoring systems.
                </li>
                <li className="legal-list-item">
                  Customer support communication.
                </li>
                <li className="legal-list-item">
                  Platform security monitoring.
                </li>
                <li className="legal-list-item">
                  Compliance with regulatory obligations.
                </li>
              </ul>
              <p className="legal-paragraph">
                We may also use aggregated or anonymized data for analytics and
                platform improvement.
              </p>
            </div>
          </div>

          {/* Section 9 - Legal Basis */}
          <div className="legal-section" id="legal-basis">
            <div className="legal-section-header">
              <span className="legal-section-number">9</span>
              <h2 className="legal-section-title">
                Legal Basis for Processing
              </h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                Where applicable, processing may rely upon:
              </p>
              <ul className="legal-list">
                <li className="legal-list-item">User consent.</li>
                <li className="legal-list-item">
                  Performance of contractual obligations.
                </li>
                <li className="legal-list-item">
                  Legal compliance requirements.
                </li>
                <li className="legal-list-item">
                  Legitimate business interests including fraud prevention and
                  service improvement.
                </li>
              </ul>
              <p className="legal-paragraph">
                Users may withdraw consent where applicable, subject to
                operational limitations.
              </p>
            </div>
          </div>

          {/* Section 10 - Data Sharing */}
          <div className="legal-section" id="data-sharing">
            <div className="legal-section-header">
              <span className="legal-section-number">10</span>
              <h2 className="legal-section-title">
                Data Sharing and Disclosure
              </h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                We do not sell personal information. Information may be shared
                only where necessary.
              </p>

              <p
                className="legal-paragraph"
                style={{ marginTop: "1.5rem", fontWeight: 600 }}
              >
                10.1 With Other Users
              </p>
              <p className="legal-paragraph">
                Certain profile information may be visible to other users to
                enable commerce, including names, listings, reviews, ratings,
                and business information. Sensitive identity documents are never
                publicly visible.
              </p>

              <p
                className="legal-paragraph"
                style={{ marginTop: "1rem", fontWeight: 600 }}
              >
                10.2 With Service Providers
              </p>
              <p className="legal-paragraph">
                We may share information with trusted partners providing payment
                processing, identity verification, cloud hosting, customer
                support services, fraud detection tools, and logistics
                coordination services. Partners are contractually required to
                maintain confidentiality and security.
              </p>

              <p
                className="legal-paragraph"
                style={{ marginTop: "1rem", fontWeight: 600 }}
              >
                10.3 With Churches or Community Partners
              </p>
              <p className="legal-paragraph">
                Limited information may be shared with verified churches solely
                for purposes such as membership verification and community
                participation analytics. Churches do not receive unrestricted
                access to personal data.
              </p>

              <p
                className="legal-paragraph"
                style={{ marginTop: "1rem", fontWeight: 600 }}
              >
                10.4 Legal Requirements
              </p>
              <p className="legal-paragraph">
                We may disclose information where required to comply with court
                orders, respond to lawful regulatory requests, prevent fraud or
                criminal activity, or protect rights or safety.
              </p>
            </div>
          </div>

          {/* Section 11 - International Data Transfers */}
          <div className="legal-section" id="international-transfers">
            <div className="legal-section-header">
              <span className="legal-section-number">11</span>
              <h2 className="legal-section-title">
                International Data Transfers
              </h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                Because the Platform operates globally, personal data may be
                processed or stored outside Kenya. Where international transfers
                occur, we implement safeguards including contractual
                protections, secure hosting environments, and industry-standard
                encryption.
              </p>
              <p className="legal-paragraph">
                Users acknowledge such transfers may occur when using the
                Platform.
              </p>
            </div>
          </div>

          {/* Section 12 - Data Retention */}
          <div className="legal-section" id="data-retention">
            <div className="legal-section-header">
              <span className="legal-section-number">12</span>
              <h2 className="legal-section-title">Data Retention</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                We retain personal information only as long as necessary to
                provide services, resolve disputes, prevent fraud, and meet
                legal obligations.
              </p>
              <p className="legal-paragraph">
                Transaction records may be retained longer where required by
                financial or regulatory laws.
              </p>
              <p className="legal-paragraph">
                Inactive accounts may be anonymized or deleted after reasonable
                retention periods.
              </p>
            </div>
          </div>

          {/* Section 13 - Data Security */}
          <div className="legal-section" id="data-security">
            <div className="legal-section-header">
              <span className="legal-section-number">13</span>
              <h2 className="legal-section-title">Data Security</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                We implement reasonable technical and organizational safeguards
                including:
              </p>
              <ul className="legal-list">
                <li className="legal-list-item">
                  Encryption during transmission.
                </li>
                <li className="legal-list-item">
                  Secure authentication systems.
                </li>
                <li className="legal-list-item">Access controls.</li>
                <li className="legal-list-item">
                  Monitoring against unauthorized access.
                </li>
                <li className="legal-list-item">Regular security reviews.</li>
              </ul>
              <p className="legal-paragraph">
                No internet transmission is completely secure, and users
                acknowledge inherent risks.
              </p>
            </div>
          </div>

          {/* Section 14 - Trust Scoring */}
          <div className="legal-section" id="trust-scoring">
            <div className="legal-section-header">
              <span className="legal-section-number">14</span>
              <h2 className="legal-section-title">
                Trust Scoring and Automated Decision Systems
              </h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                The Platform may generate reputation or trust indicators using
                automated analysis of activity such as transaction completion
                history, communication responsiveness, and customer feedback.
                These systems support safety and reliability.
              </p>
              <p className="legal-paragraph">
                Users may contact support to request review of decisions
                affecting account status where applicable.
              </p>
            </div>
          </div>

          {/* Section 15 - User Rights */}
          <div className="legal-section" id="user-rights">
            <div className="legal-section-header">
              <span className="legal-section-number">15</span>
              <h2 className="legal-section-title">User Rights</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                Subject to applicable law, users may request:
              </p>
              <ul className="legal-list">
                <li className="legal-list-item">
                  Access to personal information.
                </li>
                <li className="legal-list-item">
                  Correction of inaccurate information.
                </li>
                <li className="legal-list-item">Deletion of personal data.</li>
                <li className="legal-list-item">Restriction of processing.</li>
                <li className="legal-list-item">Objection to certain uses.</li>
              </ul>
              <p className="legal-paragraph">
                Requests may be submitted using contact details below. Identity
                verification may be required before fulfilling requests.
              </p>
            </div>
          </div>

          {/* Section 16 - Children's Privacy */}
          <div className="legal-section" id="children-privacy">
            <div className="legal-section-header">
              <span className="legal-section-number">16</span>
              <h2 className="legal-section-title">Children's Privacy</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                The Platform is not intended for individuals under eighteen (18)
                years of age.
              </p>
              <p className="legal-paragraph">
                We do not knowingly collect data from minors. Accounts
                identified as belonging to minors may be removed.
              </p>
            </div>
          </div>

          {/* Section 17 - Cookies */}
          <div className="legal-section" id="cookies">
            <div className="legal-section-header">
              <span className="legal-section-number">17</span>
              <h2 className="legal-section-title">
                Cookies and Tracking Technologies
              </h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                We may use cookies and similar technologies to maintain login
                sessions, analyze usage, and improve performance.
              </p>
              <p className="legal-paragraph">
                Users may adjust browser settings to control cookies. Certain
                features may not function properly if cookies are disabled.
              </p>
            </div>
          </div>

          {/* Section 18 - Third-Party Links */}
          <div className="legal-section" id="third-party">
            <div className="legal-section-header">
              <span className="legal-section-number">18</span>
              <h2 className="legal-section-title">Third-Party Links</h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                The Platform may contain links to external websites or services.
                We are not responsible for their privacy practices. Users should
                review third-party policies independently.
              </p>
            </div>
          </div>

          {/* Section 19 - Changes to Privacy Policy */}
          <div className="legal-section" id="changes">
            <div className="legal-section-header">
              <span className="legal-section-number">19</span>
              <h2 className="legal-section-title">
                Changes to This Privacy Policy
              </h2>
            </div>
            <div className="legal-section-content">
              <p className="legal-paragraph">
                We may update this Privacy Policy periodically. Material updates
                will be communicated through reasonable notice.
              </p>
              <p className="legal-paragraph">
                Continued use of the Platform after updates constitutes
                acceptance.
              </p>
            </div>
          </div>

          {/* Section 20 - Contact Information */}
          <div className="legal-section" id="contact">
            <div className="legal-section-header">
              <span className="legal-section-number">20</span>
              <h2 className="legal-section-title">
                Contact and Data Protection Requests
              </h2>
            </div>
            <div className="legal-section-content">
              <div className="legal-highlight">
                <p style={{ marginBottom: "1rem" }}>
                  <strong>Orra Kingdom Limited</strong>
                </p>
                <p
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    marginBottom: "0.5rem",
                  }}
                >
                  <MapPin size={16} />
                  <span>Nairobi, Republic of Kenya</span>
                </p>
                <p
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <Mail size={16} />
                  <a
                    href="mailto:hello@orrakingdom.com"
                    style={{
                      color: "inherit",
                      textDecoration: "underline",
                      textUnderlineOffset: "4px",
                    }}
                  >
                    hello@orrakingdom.com
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="legal-footer">
            <div className="legal-footer-info">
              © {new Date().getFullYear()} Orra Kingdom Limited. All rights
              reserved.
            </div>
            <div className="legal-footer-links">
              <Link to="/privacy" className="legal-footer-link">
                <Shield size={16} />
                Privacy Policy
              </Link>
              <Link to="/terms" className="legal-footer-link">
                <FileText size={16} />
                Terms
              </Link>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Back to Top Button */}
      <button
        className={`legal-back-to-top ${showBackToTop ? "visible" : ""}`}
        onClick={scrollToTop}
        aria-label="Back to top"
      >
        <ArrowUp size={20} />
      </button>
    </div>
  );
};

export default PrivacyPage;
